import { Group, HStack, VStack, Box } from '@web/components/atoms/Layout'
import { Field } from '@web/components/atoms/Field'
import { Input } from '@web/components/atoms/Input'
import { Button, IconButton } from '@web/components/atoms/Button'
import { useRef, useState } from 'react'
import { MenuItem, MenuRoot, MenuTrigger, Menu } from '@web/components/molecules/Menu'
import { DivideIcon, MinusIcon, MultiplyIcon, ParenthesesIcon, PlusIcon } from '@web/components/atoms/Icons'
import {
  CUSTOM_MERGE_TAGS,
  evaluateFormula,
  NumberFormat,
  RoundingOption,
} from '@web/components/page-components/Text/MergeTags/utils'
import { useActionDialogStore } from '@web/components/molecules/ActionDialog'
import { MergeFieldSetting } from '@web/graphql-components'
import { LexicalEditor } from 'lexical'
import { INSERT_MERGE_TAG } from './MergeTagPlugin'
import { Textarea } from '@web/components/atoms/Textarea'
import { Select } from '@web/components/atoms/Select'

type FormulaMergeTagDialogProps = {
  editor: LexicalEditor | null | undefined
  mergeTag?: { name: string; formula: string; rounding?: RoundingOption; format?: NumberFormat }
  onUpdate?: (mergeTag: Partial<MergeFieldSetting>) => void
}

const FormulaMergeTagDialog = ({ editor, mergeTag, onUpdate }: FormulaMergeTagDialogProps) => {
  const [name, setName] = useState(mergeTag?.name || '')
  const [nameError, setNameError] = useState('')
  const [formula, setFormula] = useState(mergeTag?.formula || '')
  const [formulaError, setFormulaError] = useState('')
  const [options, setOptions] = useState<{ rounding: RoundingOption; format: NumberFormat }>({
    rounding: mergeTag?.rounding ?? 'round-down',
    format: mergeTag?.format ?? '0,000',
  })
  const formulaRef = useRef<HTMLTextAreaElement>(null)

  const mergeTagName = name.split(' ').join('.')

  const addTagToFormula = (tag: string) => {
    const input = formulaRef.current
    if (!input) {
      return
    }
    const start = input.selectionStart ?? 0
    const end = input.selectionEnd ?? 0

    const prev = formula
    let newFormula: string
    let newCursorPos: number

    if (tag === '()') {
      // If there's a selection, wrap it in parentheses
      if (start !== end) {
        const selectedText = prev.substring(start, end)
        newFormula = `${prev.substring(0, start)}(${selectedText})${prev.substring(end)}`
        newCursorPos = end + 2 // Position after closing parenthesis
      } else {
        // No selection, insert () and place cursor in the middle
        newFormula = `${prev.substring(0, start)}()${prev.substring(end)}`
        newCursorPos = start + 1 // Position between the parentheses
      }
    } else {
      newFormula = `${prev.substring(0, start)}${tag}${prev.substring(end)}`
      newCursorPos = start + tag.length
    }

    setFormula(newFormula)

    setTimeout(() => {
      input.focus()
      input.setSelectionRange(newCursorPos, newCursorPos)
    }, 0)
  }

  return (
    <VStack w="100%" px="32px">
      <Field
        label="Custom Merge Tag Name"
        required={true}
        helperText={`{${name ? name.split(' ').join('.') : 'Custom.Formula'}}`}
        errorText={nameError}
        invalid={!!nameError}
      >
        <Input
          placeholder="Enter name"
          value={name}
          onChange={(e) => {
            setNameError('')
            setName(e.target.value)
          }}
        />
      </Field>
      <Group flexDirection="column" w="100%" alignItems="start">
        <Field label="Formula" required={true} errorText={formulaError} invalid={!!formulaError}>
          <HStack w="100%" justifyContent="space-between">
            <MenuRoot>
              <MenuTrigger asChild>
                <Button size="2xs" buttonAppearance="text-border">
                  <PlusIcon boxSize="10px" /> Merge Tag
                </Button>
              </MenuTrigger>
              {/* above dialog */}
              <Menu.Positioner zIndex={1401}>
                <Menu.Content>
                  <MenuItem onClick={() => addTagToFormula('[Giving.Amount]')} value="Giving.Amount">
                    Giving.Amount
                  </MenuItem>
                  <MenuItem onClick={() => addTagToFormula('[Page.AmountRaised]')} value="Page.AmountRaised">
                    Page.AmountRaised
                  </MenuItem>
                  <MenuItem
                    onClick={() => addTagToFormula('[ProgressBar.AmountRaised]')}
                    value="ProgressBar.AmountRaised"
                  >
                    ProgressBar.AmountRaised
                  </MenuItem>
                  <MenuItem onClick={() => addTagToFormula('[Campaign.AmountRaised]')} value="Campaign.AmountRaised">
                    Campaign.AmountRaised
                  </MenuItem>
                </Menu.Content>
              </Menu.Positioner>
            </MenuRoot>
            <Group gap="4px">
              <IconButton buttonAppearance="text-border" size="2xs" onClick={() => addTagToFormula(' + ')}>
                <PlusIcon boxSize="12px" />
              </IconButton>
              <IconButton buttonAppearance="text-border" size="2xs" onClick={() => addTagToFormula(' - ')}>
                <MinusIcon size={12} />
              </IconButton>
              <IconButton buttonAppearance="text-border" size="2xs" onClick={() => addTagToFormula(' * ')}>
                <MultiplyIcon size={12} />
              </IconButton>
              <IconButton buttonAppearance="text-border" size="2xs" onClick={() => addTagToFormula(' / ')}>
                <DivideIcon size={12} />
              </IconButton>
              <IconButton buttonAppearance="text-border" size="2xs" onClick={() => addTagToFormula('()')}>
                <ParenthesesIcon size={12} />
              </IconButton>
            </Group>
          </HStack>
          <Textarea
            placeholder="e.g. ([Giving.Amount] * 100) / 60"
            ref={formulaRef}
            value={formula}
            onChange={(e) => {
              setFormulaError('')
              setFormula(e.target.value)
            }}
          />
        </Field>
        <HStack w="100%" gap="16px" alignItems="flex-start">
          <Box>
            <Select
              rootProps={{ size: 'xs' }}
              value={options.rounding}
              fieldProps={{ label: 'Rounding' }}
              onChange={(e) => setOptions((prev) => ({ ...prev, rounding: e.target.value as RoundingOption }))}
            >
              <option value="round-down">Round Down</option>
              <option value="round-up">Round Up</option>
              <option value="round-nearest">Round Nearest</option>
            </Select>
          </Box>
          <Box>
            <Select
              rootProps={{ size: 'xs' }}
              value={options.format}
              fieldProps={{ label: 'Number Format' }}
              onChange={(e) => setOptions((prev) => ({ ...prev, format: e.target.value as NumberFormat }))}
            >
              <option value="0000">0000</option>
              <option value="0,000">0,000</option>
              <option value="0000.00">0.00</option>
              <option value="0,000.00">0,000.00</option>
            </Select>
          </Box>
        </HStack>
      </Group>
      <Button
        buttonAppearance="primary"
        onClick={() => {
          if (!name) {
            setNameError('Name is required')

            return
          }

          if (!formula) {
            setFormulaError('Formula is required')

            return
          }
          const result = evaluateFormula(formula, {})

          if (result === 'NaN') {
            setFormulaError('Formula is invalid')

            return
          }

          const mergeTags = Array.from(new Set((formula.match(/\[(.*?)\]/g) || []).map((tag) => tag.slice(1, -1))))
          const tagProps = {
            MergeFieldName: name,
            MergeFieldSettingId: mergeTagName,
            MergeTag: `{{${mergeTagName}}}`,
            MergeTagOptions: JSON.stringify({ formula, mergeTags, ...options }),
          }

          if (mergeTag && onUpdate) {
            editor?.update(() => {
              onUpdate(tagProps)
            })
          } else {
            editor?.dispatchCommand(INSERT_MERGE_TAG, {
              ...CUSTOM_MERGE_TAGS[0],
              ...tagProps,
            })
          }

          useActionDialogStore.getState().actions.clearDialog()
        }}
      >
        Insert
      </Button>
    </VStack>
  )
}

export default FormulaMergeTagDialog
